import type { ExtendedSelectProps, OptionType } from '../../index.types';
import React, { useMemo } from 'react';
import { components, OptionProps } from 'react-select';
import { FollowCursorTooltip } from '~/lib/ui/follow-cursor-tooltip';
import { cn } from '~/lib/utils';
import { highlightText } from '../../utils';
import { AutocompleteAvatar } from '../AutocompleteAvatar';

/**
 * Default option component for the Autocomplete.
 *
 * This component renders individual options in the autocomplete dropdown with:
 * - Avatar display (image or fallback initials)
 * - Highlighted title and description text based on search input
 * - Tooltip support for disabled options
 * - Proper accessibility and visual states
 * - Special handling for unsaved contacts with a user icon fallback
 *
 * **Note:** This is the default option renderer that can be completely replaced
 * by passing a custom `Option` component via the `components` prop in the root
 * Autocomplete component.
 *
 * **Unsaved Contact Handling:**
 * When an option has `type === 'unsaved-contact'`, the component renders a special
 * avatar with a user icon instead of the standard avatar image or initials fallback.
 * This provides visual distinction for contacts that haven't been saved to the system.
 *
 * @returns React element representing the option with avatar, title, and description
 */
export const AutocompleteOption = (
  props: OptionProps<OptionType, true>
): React.ReactElement => {
  const { data, selectProps, isDisabled } = props;
  const inputValue = selectProps.inputValue || '';

  const currentTooltipInfo = (selectProps as ExtendedSelectProps)
    .getTooltipInfo;
  const tooltipInfo = currentTooltipInfo?.(data);

  const shouldHighlight = data?.type !== 'unsaved-contact';

  const optionContent = useMemo(
    () => (
      <FollowCursorTooltip tooltipInfo={tooltipInfo}>
        {({ onMouseEnter, onMouseLeave }) => (
          <div
            id={data.type as string}
            className={cn(
              'flex items-center space-x-3 py-3',
              isDisabled && 'cursor-not-allowed opacity-50'
            )}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <AutocompleteAvatar data={data} size="md" />

            <div className="flex-1 min-w-0">
              <div className="text-[16px] font-normal truncate">
                {data?.title
                  ? shouldHighlight
                    ? highlightText(data.title, inputValue)
                    : data.title
                  : ''}
              </div>
              {data?.description && (
                <div className="text-sm truncate mt-1">
                  {shouldHighlight
                    ? highlightText(data?.description, inputValue)
                    : data?.description}
                </div>
              )}
            </div>
          </div>
        )}
      </FollowCursorTooltip>
    ),
    [tooltipInfo, data, isDisabled, inputValue, shouldHighlight]
  );

  return (
    <components.Option {...props} isDisabled={isDisabled}>
      {optionContent}
    </components.Option>
  );
};
